'use client'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faFilter,
  faMagnifyingGlass,
  faPlus,
  faRefresh,
  faTrash
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Dialog,          
  DialogTitle,      
  DialogActions 

} from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table'
import React, { useState, useEffect, useRef } from 'react'
import { Filter } from 'src/components/era-package/filters';
import ChildCard from 'src/components/era-package/shared/ChildCard'
import { useColumns } from './skumgmthooks'
import { SKUMgmtDetailModal } from '../SKUMgmtDetailModal'
import { useServerPagination } from '../../hooks/useServerPagination'
import pathMap from 'src/paths/pathMap'
import AbiSKUMgmtApiUrls from '../../../../apiUrls/AbiSKUMgmtApiUrls'
import { Link } from 'react-router-dom'
import { FileForUserGetPartyId } from '../../../../common/FileForUserCommon'


function ConfirmDeleteDialog({
  open,
  title = 'Are you sure you want to delete this record?',
  loading = false,
  onCancel,
  onConfirm,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  confirmColor = 'error'
}) {
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>{title}</DialogTitle>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          disabled={loading}
          endIcon={
            // use existing faRefresh with spin to avoid extra FA deps
            <FontAwesomeIcon icon={faRefresh} width={10} spin={loading} />
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const COLUMN_TO_PARAM = {
  skuReference: 'skuReference',
  skuDesc: 'skuDesc',
  htsNumber: 'htsNumber',
  countryOfOrigin: 'countryOfOrigin',
}

const SKU_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  partyId: '',
  skuReference: '',
  skuDesc: '',
  htsNumber: '',
  countryOfOrigin: '',
}

// Main Component
const SKUMgmtSubmissions = () => {
  const [searchInput, setSearchInput] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [selectedRow, setSelectedRow] = useState()
  const [sorting, setSorting] = useState([{ id: 'updatedDate', desc: true }])
  const columns = useColumns()
  const debounceRef = useRef(null)

  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiSKUMgmtApiUrls.LIST, {
      ...SKU_DEFAULT_PARAMS,
      partyId: FileForUserGetPartyId(),
    })

  
    const [rowToDelete, setRowToDelete] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const openDeleteForRow = (row) => {
    setRowToDelete(row?.original ?? null)
    setOpenDeleteDialog(true)
  }

  const closeDeleteDialog = () => {
    setOpenDeleteDialog(false)
    setRowToDelete(null)
  }

    

  const handleRowClick = (row) => {
    setSelectedRow(row.original)
  }

    const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data,
    columns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater
      setColumnFilters(next)
      const mapped = {}
      Object.values(COLUMN_TO_PARAM).forEach((f) => (mapped[f] = ''))
      next.forEach(({ id, value }) => {
        const key = COLUMN_TO_PARAM[id]
        if (key) mapped[key] = value ?? ''
      })
      updateParams({ ...mapped, page: 0 })
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    debugTable: false,
    debugHeaders: false,
    debugColumns: false
  })

  
  const handleDeleteRow = async (id) => {
    const res = await fetch(AbiSKUMgmtApiUrls.DELETE + `?partyId=${FileForUserGetPartyId()}&id=${id}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      }
    })
    const json = await res.json()
    if (!json?.data) {
      throw new Error('Delete failed')
    }
    return json.data
  }

  /** Confirm button in dialog */
  const confirmDelete = async () => {
    try {
      setDeleting(true)
      const id = rowToDelete?.id
      await handleDeleteRow(id)
      await fetchPage()
      closeDeleteDialog()
    } catch (err) {
      console.error(err)
    } finally {
      setDeleting(false)
    }
  }


    return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack spacing={2}>
        {/* Search and Filter Controls */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems='center'
          spacing={2}
          sx={{ mb: 2 }}
        >
                    <TextField
            size='small'
            placeholder='Search all columns...'
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value
              setSearchInput(value)
              clearTimeout(debounceRef.current)
              debounceRef.current = setTimeout(() => {
                updateParams({ search: value, page: 0 })
              }, 500)
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <FontAwesomeIcon
                    icon={faMagnifyingGlass}
                    style={{ width: 16, height: 16 }}
                  />
                </InputAdornment>
              )
            }}
            sx={{
              maxWidth: { xs: '100%', sm: '300px' },
              [`& .${outlinedInputClasses.notchedOutline}`]: {
                inset: 0
              }
            }}
          />
          <Button
            variant='outlined'
            onClick={() => setShowFilters(!showFilters)}
            startIcon={
              <FontAwesomeIcon
                icon={faFilter}
                style={{ width: 16, height: 16 }}
              />
            }
          >
            {showFilters ? 'Hide Filters' : 'Show Column Filters'}
          </Button>
          <Button
            component={Link}
            to={pathMap.ABI_SKU_MGMT_ADD_VIEW}
            variant='outlined'
            startIcon={
              <FontAwesomeIcon
                icon={faPlus}
                style={{ width: 16, height: 16 }}
              />
            }
          >
            Create SKU
          </Button>
        </Stack>
        <ChildCard sx={{ p: 0, mb: -3 }}>
          <Stack>
            <TableContainer>
              <Table>
                <TableHead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <React.Fragment key={headerGroup.id}>
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={header.id}>
                            <Typography
                              variant='body'
                              color={'primary.main'}
                              fontWeight='600'
                              {...{
                                className: header.column.getCanSort()
                                  ? 'cursor-pointer select-none'
                                  : '',
                                onClick: header.column.getToggleSortingHandler()
                              }}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                              {{
                                asc: ' 🔼',
                                desc: ' 🔽'
                              }[header.column.getIsSorted()] ?? null}
                            </Typography>
                          </TableCell>
                        ))}                      
                        <TableCell align="right" sx={{ width: 72 }}>                       
                        <Typography
                          variant="body"           
                          color={'primary.main'}
                          fontWeight="600"
                          className="select-none"   
                        >
                          Actions
                        </Typography>
                        </TableCell>
                      </TableRow>
                      {showFilters && (
                        <TableRow>
                          {headerGroup.headers.map((header) => (
                            <TableCell key={header.id}>
                              {header.column.getCanFilter() && showFilters ? (
                                <div>
                                  <Filter column={header.column} />
                                </div>
                              ) : null}
                            </TableCell>
                          ))}
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableHead>
                <TableBody>
                                    {loading && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align='center'>
                        <Typography variant='body2' color='text.secondary'>Loading...</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && data.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={columns.length + 1} align='center'>
                        <Typography variant='body2' color='text.secondary'>No SKU Template data available</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {table.getRowModel().rows.map((row) => (
                    <React.Fragment key={row.id}>
                      <TableRow
                        sx={{
                          cursor: 'pointer',
                          transition: (theme) => theme.transitions.create(),
                          '&:hover': {
                            backgroundColor: 'action.hover'
                          }
                        }}
                        onClick={() => handleRowClick(row)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            sx={cell.column.columnDef.meta?.style}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                        
                        <TableCell align="right" sx={{ width: 72 }} onClick={(e) => e.stopPropagation()} >
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100%',
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faTrash}
                                style={{ color: '#d32f2f', cursor: 'pointer' }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (deleting) return
                                  openDeleteForRow(row)

                                }}
                                role="button"
                                aria-label="Delete row"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault()
                                    openDeleteForRow(row)
                                  }
                                }}
                              />
                            </Box>
                        </TableCell>

                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Stack
              gap={1}
              p={3}
              alignItems='center'
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent='space-between'
            >
              <Box display='flex' alignItems='center' gap={1}>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => fetchPage()}
                  startIcon={
                    <FontAwesomeIcon
                      icon={faRefresh}
                      style={{ width: 16, height: 16 }}
                      spin={loading}
                    />
                  }
                >
                  Refresh data
                </Button>
                                <Typography variant='body1'>
                  {totalElements} Rows
                </Typography>
              </Box>
              <Box
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'flex'
                  }
                }}
                alignItems='center'
                gap={1}
              >
                                <Stack direction='row' alignItems='center' gap={1}>
                  <Typography variant='body1'>Page</Typography>
                  <Typography variant='body1' fontWeight={600}>
                    {pageIndex + 1} of {totalPages}
                  </Typography>
                </Stack>
                <Stack direction='row' alignItems='center' gap={1}>
                  | Go to page:
                                    <TextField
                    type='number'
                    inputProps={{ min: 1, max: totalPages }}
                    defaultValue={pageIndex + 1}
                                                            onChange={(e) => {
                      const val = Number(e.target.value)
                      if (!val || val < 1) return
                      updateParams({ page: Math.min(val, totalPages) - 1 })
                    }}
                    size='small'
                  />
                </Stack>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    updateParams({ size: Number(e.target.value), page: 0 })
                  }}
                  size='small'
                >
                  {[5, 10, 15, 20, 25].map((s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ))}
                </Select>

                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: 0 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faAnglesLeft} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: pageIndex - 1 })}
                  disabled={pageIndex === 0}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: pageIndex + 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </IconButton>
                <IconButton
                  size='small'
                  onClick={() => updateParams({ page: totalPages - 1 })}
                  disabled={pageIndex >= totalPages - 1}
                >
                  <FontAwesomeIcon icon={faAnglesRight} />
                </IconButton>
              </Box>
            </Stack>
          </Stack>
        </ChildCard>
      </Stack>
      <SKUMgmtDetailModal
        id={selectedRow?.id}
        open={!!selectedRow}
        onClose={() => setSelectedRow(null)}
        fetchData={() => fetchPage()}
        rowData={selectedRow}
      />

      <ConfirmDeleteDialog
        open={openDeleteDialog}
        loading={deleting}
        onCancel={closeDeleteDialog}
        onConfirm={confirmDelete}
        // Optional: show the skuReference (or any useful field) in the title
        title={
          'Are you sure you want to delete this record?'
        }
      />
    </LocalizationProvider>
  )
}





export default SKUMgmtSubmissions
